import { AgencyFilter } from "hassibot/component/filter/geography/types";
import { SelectableAgency } from "hassibot/component/modals/agency-filter-modal/agency-filter-modal.types";
import { Agency, MutualizedArea, MutualizedAreaUuid } from "hassibot/services_v2/common/types";

export const addSelectedStatusToAgencies = (
  selection: AgencyFilter[],
  agencies: Agency[]
): SelectableAgency[] =>
  agencies.map(agency => {
    const isIncludedAgency = selection.findIndex(a => a.value.uuid === agency.uuid) !== -1;

    return { ...agency, isSelected: isIncludedAgency };
  });

export const areAllAgenciesSelected = (agencies: SelectableAgency[]): boolean => {
  return agencies.every(agency => agency.isSelected);
};

export const NO_MUTUALIZED_AREA = "NO_MUTUALIZED_AREA" as const;

const groupAgenciesByMutualizedAreaUuid = (
  agencies: SelectableAgency[]
): Record<typeof NO_MUTUALIZED_AREA | MutualizedAreaUuid, SelectableAgency[]> => {
  const groupedAgencies = {} as Record<
    typeof NO_MUTUALIZED_AREA | MutualizedAreaUuid,
    SelectableAgency[]
  >;

  for (const agency of agencies) {
    const key = agency.mutualizedAreaUuid ?? NO_MUTUALIZED_AREA;

    if (!groupedAgencies[key]) {
      groupedAgencies[key] = [];
    }

    groupedAgencies[key].push(agency);
  }

  return groupedAgencies;
};

export const groupAndSortAgenciesByArea = (
  allAgencies: SelectableAgency[],
  mutualizedAreas: MutualizedArea[]
): {
  agenciesByMutualizedAreaUuid: Record<
    MutualizedAreaUuid | typeof NO_MUTUALIZED_AREA,
    SelectableAgency[]
  >;
  sortedAgenciesEntries: [MutualizedAreaUuid | typeof NO_MUTUALIZED_AREA, SelectableAgency[]][];
  mutualizedAreasUuidNamesMap: Record<MutualizedAreaUuid, string>;
} => {
  const sortedAgencies = allAgencies.sort((agencyA, agencyB) =>
    agencyA.name.localeCompare(agencyB.name)
  );

  const agenciesByMutualizedAreaUuid = groupAgenciesByMutualizedAreaUuid(sortedAgencies);

  const mutualizedAreasUuidNamesMap = mutualizedAreas.reduce(
    (acc, mutualizedArea) => ({
      ...acc,
      [mutualizedArea.uuid]: mutualizedArea.name,
    }),
    {} as { [mutualizedAreaUuid: string]: string }
  );

  const sortedAgenciesEntries = Object.entries(agenciesByMutualizedAreaUuid).sort(
    (area1, area2) => {
      // NO_MUTUALIZED_AREA comes first
      if (area1[0] === NO_MUTUALIZED_AREA) return -1;
      if (area2[0] === NO_MUTUALIZED_AREA) return 1;

      const area1Name = mutualizedAreasUuidNamesMap[area1[0]];
      const area2Name = mutualizedAreasUuidNamesMap[area2[0]];

      return area1Name.localeCompare(area2Name);
    }
  );

  return {
    agenciesByMutualizedAreaUuid,
    sortedAgenciesEntries,
    mutualizedAreasUuidNamesMap,
  };
};
